import React from "react";
import CasestudyTemplate, {
  Header,
  Introduction,
  Challenge,
  Body,
} from "../../../../templates/casestudyTemplate";
import Section from "../../../../components/section";
import Gallery from "../../../../components/gallery";

export default function IndexPage() {
  return (
    <CasestudyTemplate id="cmb">
      <Header id="cmb" />
      <Introduction id="cmb">
        <p>
          Opening a commercial bank account is often more complex than setting
          up a personal account.
        </p>
        <p>
          Due to strict security requirements, business customers must provide
          multiple forms of identification, engaging with various touchpoints
          throughout a multi-step process.
        </p>
        <p>
          In this case study, I focus on <strong>identity verification</strong>
          —a critical part of onboarding where strict document requirements
          often lead to customer frustration. The existing process was
          cumbersome, unclear, and lagged behind industry standards. My goal was
          to simplify this experience while maintaining compliance and security.
        </p>
      </Introduction>
      <Challenge id="cmb">
        <p>
          The bank faced a major bottleneck in its online verification process:
        </p>
        <ul>
          <li>
            <strong>High verification failure rates</strong> - Many applicants
            struggled to submit acceptable identification documents.
          </li>
          <li>
            <strong>Increased operational burden</strong> - Failed verifications
            led to more call center inquiries and in-person branch visits.
          </li>
          <li>
            <strong>Customer drop-off</strong> - Lengthy, unclear verification
            steps increased the risk of potential customers abandoning the
            process.
          </li>
          <li>
            <strong>Security vs. usability trade-off</strong> - The bank needed
            to improve the experience without compromising fraud prevention.
          </li>
        </ul>
        <p>
          To stay competitive and improve conversion rates, we needed to
          streamline identity verification while maintaining regulatory
          compliance.
        </p>
      </Challenge>
      <Body id="cmb">
        <Section>
          <p>
            As a UX consultant on loan to a large high street bank, I was asked
            to look at the commercial banking on-boarding experience, focusing
            on identy verification. This part of the onboarding process had
            fallen behind the standards found on the personal banking side as
            well as other competitors in the market.
          </p>
          <h3>Research</h3>
          <p>
            An expert review of the existing journey discovered several
            usability issues with the process:
          </p>
          <ul>
            <li>
              Instructions were only available at the start of the process in
              the form of a copy dense start page.
            </li>
            <li>
              Users were required to take photos of their documents with a
              webcam or already have them scanned and loaded onto the device
              they were using to complete the process. Although this requirement
              was stated up front, it was buried deep in the start page copy.
            </li>
            <li> Inconsistent design with other parts of the process. </li>
            <li>
              Multiple branded third party plugins were used for accessing the
              users webcam and uploading documents. These could potentially
              cause trust issues. (This was a hypothesis that we wanted to test
              with users, however there was no budget in this part of the
              project for usability testing.).
            </li>
            <li>
              Scanning the required documents was a delicate process that often
              failed due to excessive light, the wrong angles or lower quality
              webcams however the actual reason for failure was never given to
              the user. There was a theme of poor error recovery throughout the
              process After documenting the existing user flow I identified
              several areas where the system could provide the user with help
              and provide a more intuitive process.
            </li>
          </ul>
          <h3>Ideation</h3>
          <p>
            Early sketches helped me to conceptualise the idea of changing the
            context of the form from “Complete your identity verification” to
            “Do I qualify for online ID verification?”.
          </p>
        </Section>
        <Gallery
          id={"cmb"}
          images={[
            {
              source: "sketch1.jpg",
              alt: "",
            },
            {
              source: "sketch3.jpg",
              alt: "",
            },
            {
              source: "sketch2.jpg",
              alt: "",
            },
            {
              source: "sketch4.jpg",
              alt: "",
            },
          ]}
          options={{ grid: 2, width: "full", includeBackground: true }}
        />
        <Section>
          <p>
            This allowed me to create an alternative journey that asked the
            users some qualifying questions up front. These questions were
            designed to help the user understand what documents they were
            required to upload and the format the documents needed to be in. The
            qualifying questions also filtered out the users who were guaranteed
            to fail because they had the wrong documents or unsupported
            hardware. These users would still fail but they would fail early and
            gracefully. They would also be given a clearer idea of how to
            proceed.
          </p>
        </Section>
        <Gallery
          id={"cmb"}
          images={[
            {
              source: "flow.png",
              alt: "User flow diagram detailing journey logic.",
            },
          ]}
          options={{ grid: 1, width: "full", includeBackground: true }}
        />
        <Section>
          <p>
            All copy was reviewed and more specific help text and instructions
            were written to guide the user through the process and recover from
            any errors that occurred. Help was progressively disclosed to the
            user as and when they needed it.
          </p>
          <h3>Design</h3>
          <p>
            The third party plugins were difficult to work with as we had very
            little control over their design and how behaviour. I worked with a
            visual designer and the plugin developers to create an
            as-seamless-as-possible end to end journey that used the same
            branding, page furniture and tone to ensure the user felt like they
            were still in the secure environment that they started the process
            in.
          </p>
        </Section>
        <Gallery
          id={"cmb"}
          images={[
            {
              source: "wireframe1.png",
              alt: "",
            },
            {
              source: "wireframe2.png",
              alt: "",
            },
            {
              source: "wireframe3.png",
              alt: "",
            },
          ]}
          options={{ grid: 2, width: "full", includeBackground: true }}
        />
        <Section>
          <p>
            This project saw me co-locate with business analysts, security
            consultants, project managers, developers and internal design teams
            to ensure the project was kept on track and met the strict standards
            set by multiple stakeholders and the internal design system. The
            project was handed off to the client with the following
            deliverables:
          </p>
          <ul>
            <li>
              User journey flows covering the possible paths a user could take
            </li>
            <li>
              A spreadsheet with updated copy, help text and error messages
            </li>
            <li>
              A wireframed prototype that illustrated the concept of “Do I
              qualify…?”
            </li>
            <li>
              High-fidelity mock-ups to show how to maintain a consistent visual
              identity throughout the process. These designs were checked
              against the banks internal design language to make sure they
              complied.
            </li>
          </ul>
        </Section>
      </Body>
    </CasestudyTemplate>
  );
}
